import { MouseEvent, useCallback, useMemo } from 'react'

import { usePrintScale } from '@editor/hooks/usePrintScale'
import { RectangleMeshProps } from '@editor/interfaces/canvas/rectangleMesh'
import { DrawingParameterRectangle, DrawingType, PolygonSubtype } from '@editor/interfaces/drawing'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { PointArray } from '@editor/interfaces/interfaces'
import { BlueprintEditorTools, DEFAULT_STYLES, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useDrawingStateCheck from '@editor/pages/projects/blueprint-editor/hooks/useDrawingStateCheck'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import {
  addDrawing,
  removeDrawings,
  updateDrawingParameters,
} from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { convertPrintDimension } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setWorkingElementId } from '@editor/store/tool'
import { Matrix4, Vector3 } from 'three'

import { createPolygonProjection, getLastCount, getNewOrder } from '../../../utils'
import useUndo from './useUndo'

const useMainCanvas = (): CanvasConfigV2 => {
  // Hooks
  const { projectionPlane, projectionDirections, calculateOffsetMatrix } = useProjectionPlane()
  const { handleUndo } = useUndo()
  const { currentPrintScale } = usePrintScale()
  const { editingElementId, workingElementId, isSelectable } = useDrawingStateCheck()
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()

  const hiddenElementIds = useAppSelector((state) => state.toolUtil.core.hiddenElementIds)

  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const mode = useAppSelector((state) => state.blueprint.editor.mode)
  const isAllowedToModify = useAppSelector((state) => state.blueprint.editor.isAllowedToModify)

  const drawings = useAppSelector((state) => state.blueprint.drawings.document.drawings)
  const renderSkipUuids = useAppSelector((state) => state.blueprint.drawings.renderSkipUuids)
  const defaultStyles = useAppSelector((state) => state.blueprint.drawings.defaultStyles)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.Rectangle, [selectedTool])
  const isWorking = useMemo(
    () =>
      getDrawingsOfType<DrawingParameterRectangle>(drawings, DrawingType.Polygon, renderSkipUuids).some(
        (d) =>
          d.drawing_parameters.uuid === workingElementId && d.drawing_parameters.subtype === PolygonSubtype.Rectangle,
      ),
    [workingElementId, drawings, renderSkipUuids],
  )

  /**
   * Create working matrix for the rectangle from projection plane and working corner point
   */
  const getTransformationMatrix = useCallback(
    (point: PointArray) => {
      if (!projectionPlane || !projectionDirections) return null

      const origin = new Vector3(...point)

      const z = projectionDirections.normal.clone()
      const x = projectionDirections.left.clone()
      const y = projectionDirections.up.clone()

      const m = new Matrix4()
      m.makeBasis(x, y, z)
      m.setPosition(origin)

      return m
    },
    [projectionPlane, projectionDirections],
  )

  /**
   * Completes the rectangle drawing when the user releases the mouse button.
   */
  const onMouseUp = useCallback(
    (ev: MouseEvent<HTMLDivElement>, { isDragged }: CanvasEventsExtra) => {
      // Need to be in tool for adding new rectangles
      if (!isToolSelected || isDragged || isWorking || editingElementId) return

      const point = getIntersection(ev)
      if (!point) return

      const matrix = getTransformationMatrix(point)
      if (!matrix) return

      // If there is no editing element ID, set it and return (first corner)
      const uuid = crypto.randomUUID()
      dispatch(
        addDrawing({
          name: `矩形 ${
            getLastCount(
              drawings.map((p) => p.name),
              '矩形',
            ) + 1
          }`,
          drawing_type: DrawingType.Polygon,
          drawing_parameters: {
            uuid,
            subtype: PolygonSubtype.Rectangle,
            points: [point, point, point, point],
            vertices: [
              [0, 0],
              [0, 0],
              [0, 0],
              [0, 0],
            ],
            transformation: matrix.toArray(),
            order: getNewOrder(drawings),
            styles: defaultStyles,
          },
        }),
      )

      dispatch(setWorkingElementId(uuid))
    },
    [
      dispatch,
      getTransformationMatrix,
      getIntersection,
      editingElementId,
      drawings,
      isWorking,
      isToolSelected,
      defaultStyles,
    ],
  )

  return {
    objects: {
      rectangleMeshes: [
        ...useMemo(
          () =>
            getDrawingsOfType<DrawingParameterRectangle>(drawings, DrawingType.Polygon, renderSkipUuids)
              .filter((drawing) => drawing.drawing_parameters.subtype === PolygonSubtype.Rectangle)
              .map<RectangleMeshProps>(({ drawing_parameters: { uuid, transformation, points, styles, order } }) => ({
                id: uuid,
                transformation,
                positions: points,
                visible: !hiddenElementIds.includes(uuid) && mode !== Mode.Editor3D,
                color: styles?.fillColor || 'transparent',
                outline: {
                  style: styles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                  color: styles?.strokeColor,
                  thickness: convertPrintDimension(styles?.strokeWidth || 1, currentPrintScale),
                },
                isEditing: uuid === editingElementId,
                isWorking: uuid === workingElementId,
                selectable: isSelectable(uuid) && isAllowedToModify,
                offsetTransformation: calculateOffsetMatrix((order ?? 1) + 1).toArray(), // Offset by order for layering
                vertexAnchors: {
                  visible: true,
                },
                onUpdated: (positions: PointArray[]) => {
                  const { vertices, matrix } = createPolygonProjection(positions.map((p) => new Vector3(...p)))

                  dispatch(
                    updateDrawingParameters({
                      uuid,
                      points: positions,
                      vertices,
                      transformation: matrix.toArray(),
                    }),
                  )
                },
              })),
          [
            drawings,
            renderSkipUuids,
            hiddenElementIds,
            workingElementId,
            editingElementId,
            mode,
            isAllowedToModify,
            currentPrintScale,
            isSelectable,
            calculateOffsetMatrix,
            dispatch,
          ],
        ),
      ],
    },
    events: {
      // onMouseMove,
      onMouseUp,
      onUndo: useCallback(() => {
        if (!isToolSelected) return

        handleUndo()
      }, [isToolSelected, handleUndo]),
      onCancel: useCallback(() => {
        if (!isToolSelected) return

        if (workingElementId) {
          dispatch(removeDrawings([workingElementId]))
          dispatch(setWorkingElementId(undefined))
        }
      }, [isToolSelected, workingElementId, dispatch]),
    },
  }
}

export default useMainCanvas
